import { messaging, db } from '@/firebase/config'
import { getToken, onMessage } from 'firebase/messaging'
import { doc, updateDoc, arrayUnion } from '@/services/neonFirestore'

const FCM_SW_URL = '/firebase-messaging-sw.js'

/**
 * Prefer the active controlling SW (PWA /sw.js in prod, which importScripts FCM).
 * Fall back to registering firebase-messaging-sw.js when no SW exists (e.g. local non-PWA).
 */
export async function ensureServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) {
    return null
  }

  let registration = await navigator.serviceWorker.getRegistration('/')

  if (!registration) {
    registration = await navigator.serviceWorker.register(FCM_SW_URL, {
      scope: '/',
    })
  }

  registration = await navigator.serviceWorker.ready

  if (!registration.active) {
    await new Promise((resolve, reject) => {
      const worker = registration.installing || registration.waiting
      if (!worker) {
        reject(new Error('No service worker found after registration'))
        return
      }
      if (worker.state === 'activated') {
        resolve()
        return
      }
      const onChange = () => {
        if (worker.state === 'activated') {
          worker.removeEventListener('statechange', onChange)
          resolve()
        } else if (worker.state === 'redundant') {
          worker.removeEventListener('statechange', onChange)
          reject(new Error('Service worker became redundant before activation'))
        }
      }
      worker.addEventListener('statechange', onChange)
    })
  }

  return registration
}

export const notificationService = {
  async requestPermission(userId) {
    if (!messaging) {
      console.warn('Messaging not initialized')
      return null
    }

    if (typeof Notification === 'undefined') {
      console.warn('Notification API not supported')
      return null
    }

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
    if (!vapidKey) {
      console.error('VITE_FIREBASE_VAPID_KEY is missing in .env')
      return null
    }

    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        return null
      }

      const registration = await ensureServiceWorkerRegistration()
      if (!registration?.active) {
        console.warn('No active service worker; cannot retrieve FCM token.')
        return null
      }

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      })

      if (token) {
        if (userId) {
          await this.saveTokenToUser(userId, token)
        }
        return token
      }

      console.warn('No registration token available.')
      return null
    } catch (error) {
      console.error('An error occurred while retrieving token:', error)
      return null
    }
  },

  async saveTokenToUser(userId, token) {
    if (!userId || !token) return

    try {
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, {
        fcmToken: token,
        fcmTokens: arrayUnion(token),
        notificationsEnabled: true,
        lastTokenUpdate: new Date(),
      })
    } catch (error) {
      console.error('Error saving token to Firestore:', error)
    }
  },

  listenForMessages(onForeground) {
    if (!messaging) return () => {}

    return onMessage(messaging, (payload) => {
      console.log('[notificationService] Foreground message received:', payload)
      if (typeof onForeground === 'function') {
        onForeground(payload)
      }
    })
  },
}
